import React from 'react';

import { CssVars, Type } from '@robinhood/design';
import { useUniqueId } from '@robinhood/design/product/components/useUniqueId';
import { SCREENREADER_ONLY_STYLES } from '@robinhood/design/util/commonStyles';

import Cell from './Cell';
import { LayoutStyle } from 'src/constants/styles';

const RADIO_BUTTON_PIXEL_SIZE = 16;

interface RadioButtonProps {
  label: React.ReactNode;
  state?: 'default' | 'disabled' | false;
  value: string;
  id?: string;
}

export const RadioButton = ({ label, id, state = 'default', value }: RadioButtonProps) => {
  const { name, onChange, value: selectedValue } = useRadioButtonGroupContext();
  const isChecked = selectedValue === value;
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const onClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    inputRef.current?.click();
    inputRef.current?.focus();
  }, []);

  return (
    <Cell css={[LayoutStyle.rowACJB]} onClick={onClick}>
      <div>
        <Type.Text size='M'>{label}</Type.Text>
      </div>
      <div css={LayoutStyle.inlineBlock}>
        <input
          ref={inputRef}
          checked={isChecked}
          // NOTE(jare): We render this radio button to get a bunch of built in browser
          // behaviors for radio inputs (e.g. keyboard navigation, mouse events on labels),
          // then use CSS to style a sibling element to look like the design system radio
          // button
          css={SCREENREADER_ONLY_STYLES}
          disabled={state === 'disabled'}
          id={id}
          name={name}
          onChange={onChange}
          type='radio'
          value={value}
        />
        <div
          // NOTE(jare): We hide this div from screen readers since it's only meant to
          // visually indicate the status of the radio button
          aria-hidden='true'
          css={[
            {
              backgroundColor: CssVars.neutral.bg1,
              borderColor: CssVars.neutral.fg2,
              borderRadius: '50%',
              borderStyle: 'solid',
              borderWidth: 2,
              boxSizing: 'border-box',
              height: RADIO_BUTTON_PIXEL_SIZE,
              width: RADIO_BUTTON_PIXEL_SIZE,
            },
            {
              'input:active + &, &:active': {
                borderColor: CssVars.neutral.fg3,
              },
              'input:disabled + &': {
                backgroundColor: CssVars.neutral.bg2,
                borderColor: CssVars.neutral.bg3,
              },
              'input:hover + &, &:hover': {
                borderColor: CssVars.neutral.fg1,
              },
            },
            isChecked && {
              borderColor: CssVars.neutral.fg1,
              borderWidth: 5,

              'input:active + &, &:active': {
                borderColor: CssVars.neutral.fg1,
              },
              'input:disabled + &': {
                borderColor: CssVars.neutral.fg3,
              },
              'input:hover + &, &:hover': {
                borderColor: CssVars.neutral.fg1,
              },
            },
          ]}
        />
      </div>
    </Cell>
  );
};

type RadioButtonGroupContextValue = {
  name: string;
  value: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
const RadioButtonGroupContext = React.createContext<RadioButtonGroupContextValue | null>(null);

interface RadioButtonGroupProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: string | null;
}
export const RadioButtonGroup = ({ children, onChange, value }: React.PropsWithChildren<RadioButtonGroupProps>) => {
  const name = useUniqueId();
  const contextValue = React.useMemo(() => ({ name, onChange, value }), [name, onChange, value]);
  return <RadioButtonGroupContext.Provider value={contextValue}>{children}</RadioButtonGroupContext.Provider>;
};

export const useRadioButtonGroupContext = (): RadioButtonGroupContextValue => {
  const value = React.useContext(RadioButtonGroupContext);
  if (!value) {
    throw new Error('This component must be rendered inside of a <RadioButtonGroup>');
  }
  return value;
};
